(function () {
  'use strict';

  if (window.customElements.get('orli-lead-popup')) return;

  class OrliLeadPopup extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this.dialog = this.querySelector('[data-orli-lead-dialog]');
      this.overlay = this.querySelector('[data-orli-lead-overlay]');
      this.closeButtons = Array.from(this.querySelectorAll('[data-orli-lead-close]'));
      this.form = this.querySelector('form');
      this.submitButton = this.querySelector('[data-orli-lead-submit]');
      this.loader = this.querySelector('[data-orli-lead-loader]');
      this.success = this.querySelector('[data-orli-lead-success]');
      this.storageKey = 'orli:lead-popup:' + (this.dataset.sectionId || 'default');
      this.lastFocusedElement = null;
      this.triggerTimer = null;
      this.isOpen = false;
      this.onKeydown = this.onKeydown.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.onExitIntent = this.onExitIntent.bind(this);
      this.onFormSubmit = this.onFormSubmit.bind(this);
      this.openFromEvent = this.openFromEvent.bind(this);

      this.closeButtons.forEach(
        function (button) {
          button.addEventListener('click', this.close.bind(this));
        }.bind(this)
      );

      if (this.overlay) {
        this.overlay.addEventListener(
          'click',
          function () {
            if (this.dataset.closeOnOverlay === 'true') this.close();
          }.bind(this)
        );
      }

      if (this.form) this.form.addEventListener('submit', this.onFormSubmit);
      document.addEventListener('orli:lead-popup:open', this.openFromEvent);

      if (this.success || this.dataset.postedSuccessfully === 'true') {
        this.setSuppression(Number(this.dataset.successExpiry) || 180);
        this.open(true);
        return;
      }

      if (this.dataset.mode === 'testing' || window.Shopify && Shopify.designMode) {
        this.scheduleTrigger(true);
        return;
      }

      if (!this.isSuppressed()) this.scheduleTrigger(false);
    }

    disconnectedCallback() {
      this.clearTriggers();
      if (this.form) this.form.removeEventListener('submit', this.onFormSubmit);
      document.removeEventListener('orli:lead-popup:open', this.openFromEvent);
      document.removeEventListener('keydown', this.onKeydown);
      this._connected = false;
    }

    scheduleTrigger(testing) {
      var trigger = this.dataset.trigger || 'delay';
      var delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;
      if (testing) delay = Math.min(delay, 700);

      if (trigger === 'delay' || trigger === 'delay_or_scroll') {
        this.triggerTimer = window.setTimeout(
          function () {
            this.open();
          }.bind(this),
          delay
        );
      }

      if (trigger === 'scroll' || trigger === 'delay_or_scroll') {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        this.onScroll();
      }

      if (trigger === 'exit_intent') {
        document.addEventListener('mouseout', this.onExitIntent);
      }
    }

    onScroll() {
      var documentHeight = Math.max(document.documentElement.scrollHeight - window.innerHeight, 1);
      var progress = window.scrollY / documentHeight * 100;

      if (progress >= Math.max(10, Number(this.dataset.scrollDepth) || 40)) {
        this.open();
      }
    }

    onExitIntent(event) {
      if (event.clientY <= 8 && !event.relatedTarget) this.open();
    }

    openFromEvent() {
      this.open(true);
    }

    open(force) {
      if (this.isOpen) return;
      if (!force && this.dataset.mode !== 'testing' && this.isSuppressed()) return;

      this.clearTriggers();
      this.isOpen = true;
      this.lastFocusedElement = document.activeElement;
      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('orli-lead-popup-open');
      document.addEventListener('keydown', this.onKeydown);

      (window.requestAnimationFrame || function (callback) {
        window.setTimeout(callback, 0);
      })(
        function () {
          var focusTarget = this.success || this.querySelector('input:not([type="hidden"])') || this.dialog;
          if (focusTarget && typeof focusTarget.focus === 'function') {
            focusTarget.focus({ preventScroll: true });
          }
        }.bind(this)
      );
    }

    close() {
      if (!this.isOpen) return;
      this.isOpen = false;
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('orli-lead-popup-open');
      document.removeEventListener('keydown', this.onKeydown);

      if (this.dataset.mode !== 'testing' && !this.success && this.dataset.postedSuccessfully !== 'true') {
        this.setSuppression(Number(this.dataset.closeExpiry) || 7);
      }

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus({ preventScroll: true });
      }
    }

    onKeydown(event) {
      if (!this.isOpen) return;

      if (event.key === 'Escape' && this.dataset.closeOnEsc === 'true') {
        event.preventDefault();
        this.close();
        return;
      }

      if (event.key !== 'Tab') return;

      var focusable = Array.from(
        this.dialog.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        )
      ).filter(function (element) {
        return !element.hidden && element.offsetParent !== null;
      });

      if (!focusable.length) {
        event.preventDefault();
        this.dialog.focus();
        return;
      }

      var first = focusable[0];
      var last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }

    onFormSubmit() {
      if (!this.form || !this.form.checkValidity()) return;
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-busy', 'true');
        var label = this.submitButton.querySelector('span:not([data-orli-lead-loader])');
        if (label) label.hidden = true;
      }
      if (this.loader) this.loader.hidden = false;
    }

    clearTriggers() {
      window.clearTimeout(this.triggerTimer);
      window.removeEventListener('scroll', this.onScroll);
      document.removeEventListener('mouseout', this.onExitIntent);
    }

    isSuppressed() {
      try {
        var expiresAt = Number(window.localStorage.getItem(this.storageKey) || 0);
        if (!expiresAt) return false;
        if (expiresAt <= Date.now()) {
          window.localStorage.removeItem(this.storageKey);
          return false;
        }
        return true;
      } catch (error) {
        return false;
      }
    }

    setSuppression(days) {
      try {
        var expiresAt = Date.now() + Math.max(1, Number(days) || 1) * 24 * 60 * 60 * 1000;
        window.localStorage.setItem(this.storageKey, String(expiresAt));
      } catch (error) {}
    }
  }

  window.customElements.define('orli-lead-popup', OrliLeadPopup);
})();
